/*
 * Paid Ads Analysis — used by the CXO, Paid Ads and Academic Head panels.
 * Same bar chart per ad type (Leads / Activity / Paid) and the same table with a Total row.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from "chart.js";
import { Bar } from "react-chartjs-2";
import { getExamListNew, getParentCourseNew } from "../../Action/courseAction";
import { getPaidAdsAnalysis } from "../../Action/questionBankAction";
import { CustomAdminTable, CustomPageLayout } from "../../Component/customComponents";
import { TableTitle, formatDateRange, todayISO } from "../Common/transactionCommon";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const TYPE_NAMES = { 1: "Search Ads", 2: "Display Ads", 3: "Common Search Ads", 4: "Meta Ads" };
const typeName = (id) => TYPE_NAMES[id] || "Ads";
const n = (v) => Number(v) || 0;
const fmt = (v) => n(v).toLocaleString("en-IN");
// same chart look as before
const BAR_BG = ["rgba(53, 162, 235, 0.5)", "rgba(255, 159, 64, 0.5)", "rgba(75, 192, 153, 0.5)", "rgba(153, 102, 255, 0.5)"];
const BAR_BORDER = ["rgb(53, 162, 235)", "rgb(255, 159, 64)", "rgb(75, 192, 153)", "rgb(153, 102, 255)"];
const OPTIONS = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, title: { display: false } }, scales: { y: { beginAtZero: true } } };

const PaidAdsAnalysis = () => {
  const dispatch = useDispatch();
  const [examList, setExamList] = useState([]);
  const [streamList, setStreamList] = useState([]);
  const [filters, setFilters] = useState(() => ({ startDate: todayISO(), endDate: todayISO(), exam: "", examStream: "" }));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /** same request as before: { startDate, endDate, examTypeId?, baseCourseId? } */
  const fetchData = (f) => {
    setLoading(true);
    dispatch(
      getPaidAdsAnalysis(
        { startDate: f.startDate, endDate: f.endDate, ...(f.exam && { examTypeId: f.exam._id }), ...(f.examStream && { baseCourseId: f.examStream._id }) },
        (res) => {
          setRows((res?.data || []).filter((i) => [1, 2, 3, 4].includes(i?._id))); // only the 4 ad types (as before)
          setLoading(false);
        },
        () => {
          setRows([]);
          setLoading(false);
        },
      ),
    );
  };

  useEffect(() => {
    dispatch(getExamListNew((res) => setExamList(res || [])));
    fetchData(filters); // today on open, as before
  }, []);

  // same totals as before
  const totalRow = useMemo(() => {
    if (!rows.length) return null;
    const s = (k) => rows.reduce((t, r) => t + n(r?.[k]), 0);
    const users = s("users");
    const activity = s("activityCount");
    const paid = s("paidUsersCount");
    const budget = s("budget");
    const amount = s("totalAmount");
    return {
      _total: true,
      _id: "total",
      users,
      activityCount: activity,
      activityConversion: ((activity / users) * 100 || 0).toFixed(2),
      paidUsersCount: paid,
      budget,
      roi: `${(budget > 0 ? ((amount - budget) / budget) * 100 : 0).toFixed(2)}%`,
      totalAmount: amount,
      paidConversion: ((paid / users) * 100 || 0).toFixed(2),
    };
  }, [rows]);

  const bold = (r, v) => (r._total ? <strong>{v}</strong> : v);
  const columns = [
    { key: "_id", header: "Type", minWidth: 150, render: (v, r) => <strong>{r._total ? "Total" : typeName(v)}</strong> },
    { key: "users", header: "Lead Count", align: "right", render: (v, r) => bold(r, fmt(v)) },
    { key: "activityCount", header: "Activity", align: "right", render: (v, r) => bold(r, fmt(v)) },
    { key: "activityConversion", header: "Activity Conversion", align: "right", render: (v, r) => bold(r, `${v || 0}%`) },
    { key: "paidUsersCount", header: "Paid Users", align: "right", render: (v, r) => bold(r, fmt(v)) },
    { key: "budget", header: "Budget", align: "right", render: (v, r) => bold(r, fmt(v)) },
    { key: "roi", header: "ROI", align: "right", render: (v, r) => bold(r, v || 0) },
    { key: "totalAmount", header: "Total Paid Amount", align: "right", minWidth: 130, render: (v, r) => bold(r, `₹ ${fmt(v)}`) },
    { key: "paidConversion", header: "Paid Conversion", align: "right", render: (v, r) => bold(r, `${v || 0}%`) },
  ];

  const filterFields = useMemo(
    () => [
      { key: "startDate", label: "Start Date", type: "date", half: true, isRequired: true, defaultValue: todayISO },
      { key: "endDate", label: "End Date", type: "date", half: true, isRequired: true, defaultValue: todayISO, minDate: (v) => v.startDate, validate: (value, v) => (value && v.startDate && value < v.startDate ? "End date can't be before the start date" : "") },
      { key: "exam", label: "Exam Name", type: "dropdown", options: examList, clears: ["examStream"] },
      { key: "examStream", label: "Exam Stream", type: "dropdown", options: streamList, disabled: (v) => !v.exam, hint: (v) => (!v.exam ? "Select an exam first" : undefined) },
    ],
    [examList, streamList],
  );

  return (
    <CustomPageLayout title="PAID ADS ANALYSIS">
      <div className="d-flex flex-column gap-3">
        {rows.length > 0 && (
          <div className="row g-3">
            {rows.map((item) => (
              <div key={item._id} className="col-xl-3 col-md-6 col-12">
                <section className="bg-white border rounded-3 shadow-sm p-2 h-100">
                  <h6 className="text-center mt-1 mb-2 text-secondary">{typeName(item._id)}</h6>
                  <div style={{ height: 230 }}>
                    <Bar
                      options={OPTIONS}
                      data={{
                        labels: ["Leads", "Activity", "Paid"],
                        datasets: [
                          {
                            label: typeName(item._id),
                            data: [item?.users ?? item?.totalUsers, item?.activityCount ?? item?.totalActivityCount, item?.paidUsersCount ?? item?.totalPaidUsers],
                            backgroundColor: BAR_BG,
                            borderColor: BAR_BORDER,
                            borderWidth: 1,
                          },
                        ],
                      }}
                    />
                  </div>
                </section>
              </div>
            ))}
          </div>
        )}
        <CustomAdminTable
          id="paid-ads-analysis"
          title={<TableTitle title={`Ad Types (${rows.length})`} from={filters.startDate} to={filters.endDate} />}
          columns={columns}
          data={totalRow ? [...rows, totalRow] : []}
          loading={loading}
          rowKey={(r, i) => r?._id ?? i}
          rowClassName={(r) => (r._total ? "cat-rowGrandTotal" : "")}
          advancedFilters={filterFields}
          advancedFilterValues={filters}
          onApplyFilters={(values) => {
            setFilters(values);
            fetchData(values);
          }}
          applyFiltersOnReset
          onFilterChange={(key, value) => {
            if (key !== "exam") return;
            setStreamList([]);
            if (value) dispatch(getParentCourseNew(`${value._id}`, (res) => setStreamList(res || [])));
          }}
          filterSubtitle="Pick the dates, optionally an exam and stream"
          emptyTitle="No records"
          emptyDescription={`Nothing for ${formatDateRange(filters.startDate, filters.endDate)}.`}
        />
      </div>
    </CustomPageLayout>
  );
};

export default PaidAdsAnalysis;
